import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';

export const TermsOfServicePage = () => {
  usePageMeta({
    title: 'Terms of Service',
    description: 'Digitive Health terms of service. Review the terms and conditions governing use of our healthcare staffing platform.',
    path: '/terms-of-service',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      {/* Header */}
      <Header />

      {/* Content */}
      <section className="pt-28 pb-16">
        <div className="container-width">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-8">
              Terms of Service
            </h1>
            <p className="text-muted-foreground mb-8">
              Last Updated: February 2025
            </p>

            <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Agreement to Terms</h2>
                <p>
                  By accessing or using the services of Digitive Health ("Company," "we," "us," or "our"), 
                  including our website, staffing services, and any related services, you agree to be bound by 
                  these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Healthcare Staffing Services</h2>
                <h3 className="text-xl font-medium text-foreground mb-3">For Healthcare Professionals</h3>
                <p>By registering with Digitive Health as a healthcare professional, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate and complete information regarding your qualifications, credentials, and work history</li>
                  <li>Maintain current and valid professional licenses and certifications</li>
                  <li>Notify us immediately of any changes to your licensure status or any disciplinary actions</li>
                  <li>Comply with all applicable federal, state, and local laws and regulations</li>
                  <li>Complete all required background checks, drug screenings, and health assessments</li>
                  <li>Adhere to the policies and procedures of assigned healthcare facilities</li>
                  <li>Maintain patient confidentiality in accordance with HIPAA regulations</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">For Healthcare Facilities</h3>
                <p>By engaging Digitive Health for staffing services, healthcare facilities agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate job descriptions and staffing requirements</li>
                  <li>Maintain a safe working environment for temporary staff</li>
                  <li>Not directly hire our placed professionals during or within 12 months after an assignment without prior written consent</li>
                  <li>Pay invoices in accordance with agreed-upon terms</li>
                  <li>Comply with all applicable employment laws and regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Professional Conduct</h2>
                <p>
                  All healthcare professionals placed through our services are expected to maintain the highest 
                  standards of professional conduct, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Providing competent care within the scope of their licensure</li>
                  <li>Treating patients, colleagues, and facility staff with respect and dignity</li>
                  <li>Following infection control protocols and safety procedures</li>
                  <li>Documenting care accurately and timely</li>
                  <li>Reporting any incidents, errors, or concerns through appropriate channels</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Compensation and Benefits</h2>
                <p>
                  Compensation rates, pay schedules, and benefits will be specified in individual assignment 
                  agreements. Healthcare professionals are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Accurately reporting hours worked</li>
                  <li>Submitting timesheets by required deadlines</li>
                  <li>Understanding and complying with tax obligations</li>
                  <li>Reviewing and understanding benefit eligibility requirements</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Confidentiality</h2>
                <p>
                  All parties agree to maintain the confidentiality of:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Protected health information (PHI) in compliance with HIPAA</li>
                  <li>Proprietary business information</li>
                  <li>Compensation and contract terms</li>
                  <li>Trade secrets and business practices</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Intellectual Property</h2>
                <p>
                  All content on our website, including text, graphics, logos, and software, is the property 
                  of Digitive Health and is protected by intellectual property laws. You may not use, 
                  reproduce, or distribute our content without prior written permission.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by law, Digitive Health shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages arising from the use of 
                  our services. Our total liability shall not exceed the fees paid for the specific services 
                  giving rise to the claim.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Indemnification</h2>
                <p>
                  You agree to indemnify and hold harmless Digitive Health, its officers, directors, 
                  employees, and agents from any claims, damages, losses, or expenses arising from your 
                  violation of these Terms of Service or your use of our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">9. Termination</h2>
                <p>
                  Either party may terminate the relationship at any time with appropriate notice as specified 
                  in individual agreements. Digitive Health reserves the right to immediately terminate 
                  services for violations of these terms, professional misconduct, or failure to maintain 
                  required credentials.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">10. Governing Law</h2>
                <p>
                  These Terms of Service shall be governed by and construed in accordance with the laws of 
                  the State of California, without regard to its conflict of law provisions. Any disputes 
                  shall be resolved in the courts of Alameda County, California.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">11. Contact Information</h2>
                <p>
                  For questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-secondary rounded-lg">
                  <p><strong>Digitive Health Inc.</strong></p>
                  <p>11501 Dublin Blvd STE 200</p>
                  <p>Dublin, CA 94568</p>
                  <p>Phone: (408) 935-8699</p>
                  <p>Email: sales@digitivehealth.com</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">12. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                  immediately upon posting to our website. Your continued use of our services after any changes 
                  constitutes acceptance of the new terms.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
